<template>
  <div :class="[active.loaded ? '' : 'tw-invisible', { 'tw-dark-layout': isDarkTheme }]"
       class="forgot-page">
    <!-- 內容（教會可用且存在） -->
    <div v-if="active.data.available && active.data.organization.uuid"
         class="tw-flex tw-min-h-screen tw-w-full tw-overflow-hidden tw-bg-white dark:tw-bg-[#161d31]">

      <!-- 品牌 Logo -->
      <a class="tw-absolute tw-left-4 tw-top-4 tw-z-20">
        <img class="tw-h-12 tw-w-12 tw-rounded-md tw-object-cover"
             alt="logo"
             :src="getLogo" />
      </a>

      <!-- 左側：背景圖（大螢幕才顯示） -->
      <div class="tw-relative tw-hidden lg:tw-block lg:tw-flex-1">
        <div class="tw-absolute tw-inset-0 tw-z-[2] tw-bg-white/[0.55] dark:tw-bg-white/[0.25]" />
        <div class="tw-absolute tw-inset-0 tw-z-[1] tw-bg-cover tw-bg-center tw-bg-no-repeat"
             :style="{ backgroundImage: `url(${ getBackground })` }" />
      </div>

      <!-- 右側：表單面板 -->
      <div class="tw-relative tw-flex tw-w-full tw-flex-col tw-justify-center tw-bg-white tw-px-6 tw-py-10 dark:tw-bg-[#343d55] lg:tw-w-[33.333%] lg:tw-min-w-[420px]">
        <div class="tw-mx-auto tw-w-full tw-max-w-md">
          <h2 class="tw-mb-6 tw-text-center tw-text-2xl tw-font-bold tw-text-[#dda026]">
            {{ active.data.name }} - {{ $t('忘記密碼') }}
          </h2>

          <form @submit.prevent="onSubmit"
                class="tw-flex tw-flex-col tw-gap-4">
            <!-- 別移除, 讓 form 可以用 Enter 自動送出 -->
            <button type="submit" class="tw-hidden" />

            <!-- 帳號 -->
            <div>
              <label class="f-label">{{ $t('帳號') }}</label>
              <input v-model="field.account"
                     type="text"
                     autocomplete="off"
                     :placeholder="$t('請輸入管理員帳號')"
                     class="f-input"
                     :class="{ 'f-input--error': errorIn([1001, 1002]) }"
                     @input="active.error = 0" />
              <p v-if="errorIn([1001, 1002])"
                 class="f-error"
                 v-safe-html="$t(`${ configure.prefix }.error.${ active.error }.subject`)" />
            </div>

            <!-- 電子郵件 -->
            <div>
              <label class="f-label">{{ $t('電子郵件') }}</label>
              <input v-model="field.email"
                     type="text"
                     autocomplete="off"
                     :placeholder="$t('請輸入管理員電子郵件')"
                     class="f-input"
                     :class="{ 'f-input--error': errorIn([1003, 1004]) }"
                     @input="active.error = 0" />
              <p v-if="errorIn([1003, 1004])"
                 class="f-error"
                 v-safe-html="$t(`${ configure.prefix }.error.${ active.error }.subject`)" />
            </div>

            <!-- 驗證碼（圖形） -->
            <div>
              <label class="f-label">{{ $t('驗證碼') }}</label>
              <div class="tw-flex tw-items-stretch tw-gap-2">
                <input v-model="field.captcha"
                       type="text"
                       autocomplete="off"
                       :maxlength="active.minimum.captcha"
                       :placeholder="$t('請輸入圖形中的文字')"
                       class="f-input tw-flex-1"
                       :class="{ 'f-input--error': errorIn([1005, 1006, 1007]) }"
                       @input="active.error = 0" />
                <img :src="getCaptcha"
                     alt="captcha"
                     :title="$t('點擊更換驗證碼')"
                     @click="onGenerateCaptcha"
                     class="tw-h-[46px] tw-w-[120px] tw-cursor-pointer tw-rounded-lg tw-border tw-border-[#d8d6de] tw-object-cover dark:tw-border-[#3b4253]" />
              </div>
              <p v-if="errorIn([1005, 1006, 1007])"
                 class="f-error"
                 v-safe-html="$t(`${ configure.prefix }.error.${ active.error }.subject`)" />
            </div>

            <!-- 送出 -->
            <button type="button"
                    :disabled="active.xhring"
                    @click="onSubmit"
                    class="f-btn tw-mt-2"
                    v-safe-html="$t(`submit.${ active.xhring ? 'waiting' : 'forgot.found' }`)" />
          </form>
        </div>

        <!-- 底部：主題 / 語言 / 返回登入 -->
        <div class="tw-mx-auto tw-mt-8 tw-flex tw-w-full tw-max-w-md tw-items-center tw-justify-between">
          <div class="tw-flex tw-items-center tw-gap-2.5">
            <!-- 主題切換 -->
            <el-tooltip :content="$t(isDarkTheme ? '深色主題' : '淺色主題')"
                        placement="top-start">
              <icon-sax @click="setTheme(isDarkTheme ? EnumTheme.LIGHT : EnumTheme.DARK)"
                        class="tw-cursor-pointer ft-20"
                        :class="isDarkTheme ? 'tw-text-[#dda026]' : 'tw-text-[#6e6b7b]'"
                        :type="isDarkTheme ? 'sun' : 'moon'" />
            </el-tooltip>
            <!-- 語言 -->
            <el-dropdown popper-class="light-language"
                         placement="top-start">
              <icon-sax class="tw-cursor-pointer tw-text-[#6e6b7b] ft-20"
                        type="global" />
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item v-for="j in getAllRegion"
                                    :key="j.id"
                                    @click="setRegion(j.id)">
                    <span class="tw-flex tw-items-center">
                      <country-flag :iso="j.id" />
                      <span class="tw-ml-2.5 tw-text-xs"
                            :class="getCurrentRegion.id === j.id ? 'tw-text-[#5e5873]' : 'tw-text-[#b9b9c3]'"
                            v-safe-html="j.value" />
                    </span>
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
          <!-- 返回登入 -->
          <el-tooltip :content="$t('返回登入')"
                      placement="top-end">
            <router-link :to="{ name: 'Login' }">
              <icon-sax class="tw-cursor-pointer tw-text-[#6e6b7b] ft-20"
                        type="arrow-right" />
            </router-link>
          </el-tooltip>
        </div>
      </div>
    </div>

    <!-- 教會不存在 / 不可用 -->
    <error404 :home="false"
              v-else />
  </div>
</template>

<script setup>

/**
 * 忘記密碼 - 送出帳號 + 電子郵件 + 圖形驗證碼
 *
 * 純 Tailwind + JS 重構：移除 FormInput / FormCaptcha 等包裝元件，改用原生 input + Tailwind；
 * 深色模式以 dark: 標記（根節點依 isDarkTheme 反應式掛 tw-dark-layout）。
 *
 * API 與 input/output 完全沿用原 /api/forgot（後端已扁平化為 kplug）：
 *   - 成功回應為 ok() 殼 {code:200, ..., data:{uuid}}，以 code===200 判成功。
 *   - 驗證失敗回 HTTP 200 {code:1001~1008, message:{subject,text}}，本頁依 code 就地標紅欄位；
 *     code>=900 時 @/api 攔截器也會自動彈出 subject/text（沿用原行為）。
 *
 * 因管理員電子郵件早期可空白、也無唯一值，需以「帳號 + 電子郵件」對應，避免洗錯人。
 */

import IconSax from "@/component/IconSax.vue"
import Error404 from "@/component/Error.vue"
import CountryFlag from "@/component/CountryFlag.vue"

import { computed, onMounted, reactive } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useI18n } from "vue-i18n"
import { createLogin } from "@/api/module/session"
import http from "@/api"
import { useSetting } from "@/hook/useSetting"
import { usePermission } from "@/hook/usePermission"
import { useRegion } from "@/hook/useRegion"
import { useSwalAlert } from "@/hook/useSwalAlert"
import { useUtils } from "@/hook/useUtils"
import { EnumTheme } from "@/enum/theme"
import { RouteNameEnum } from "@/v2/enumerate/route"

// API（僅本頁使用 → 內聯實作，不另立 api/module）：忘記密碼 PUT /api/forgot
// builtIn.failure=false → 由頁面自行處理錯誤（驗證訊息要留在原頁顯示）
const apiForgotCreate = (params) => http.put(`/api/forgot`, params, { opts: { builtIn: { failure: false } } })

const route = useRoute()
const router = useRouter()

const { t } = useI18n()
const { showWarning } = useSwalAlert()
const { isLogin } = usePermission()
const { setTheme, isDarkTheme } = useSetting()
const { setRegion, getAllRegion, getCurrentRegion } = useRegion()
const { getUuid } = useUtils()

/**
 * 參數 - 頁面 - 配置
 */
const configure = reactive({
  // i18n 代碼前置
  prefix: "page.forgot",
})

/**
 * 參數 - 本地
 */
const active = reactive({
  loaded: false,
  error: 0,
  success: 0,
  xhring: false,
  // 圖形驗證碼刷新識別碼
  captcha: getUuid(16, true),
  minimum: {
    // 帳號
    account: 2,
    // 驗證碼
    captcha: parseInt(import.meta.env.VITE_SITE_CAPTCHA_LENGTH),
  },
  // 登入頁面資訊
  data: {
    organization: { seq: null, uuid: null, name: null },
    available: true,
    name: null,
    uuid: null,
    avatarUrl: null,
  },
})

/**
 * 欄位
 */
const field = reactive({
  account: null,
  email: null,
  captcha: null,
})

/**
 * 錯誤代碼是否落在指定欄位範圍
 */
const errorIn = (codes) => codes.includes(active.error)

/**
 * 提交
 */
const onSubmit = async () => {
  // 效驗 - 帳號
  if (!field.account) return (active.error = 1001)
  // 效驗 - 帳號 - 長度
  if (field.account.length < active.minimum.account) return (active.error = 1002)
  // 效驗 - 郵件
  if (!field.email) return (active.error = 1003)
  // 效驗 - 郵件 - 格式
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.email)) return (active.error = 1004)
  // 效驗 - 驗證碼
  if (!field.captcha) return (active.error = 1005)
  // 效驗 - 驗證碼 - 長度
  if (field.captcha.length !== active.minimum.captcha) return (active.error = 1006)

  // 標示 - 進行中
  active.xhring = true

  const { data } = await apiForgotCreate({
    ...field,
    // 圖形驗證碼識別碼（後端以此為 key 從快取取答案，取代 HttpSession）
    rt: active.captcha,
    church: active.data.uuid,
  })

  // 無法連線 / 伺服器錯誤（攔截器回 false）
  if (data === undefined) {
    return showWarning({
      subject: t("暫時無法使用"),
      text: t("請稍後再試或請聯絡管理人員"),
      completion: { done: () => (active.xhring = false) },
    })
  }

  // 正常（前往確認驗證碼頁）
  if (data.code === 200) {
    return router.push({
      name: "ForgotVerification",
      params: { id: route.params.id, uuid: data.data.uuid },
    })
  }

  // 驗證失敗（攔截器已彈出 subject/text）：找不到管理員時清空帳號與郵件
  if (data.code === 1008) {
    field.account = null
    field.email = null
  }
  // 請求已送達後端 = 驗證碼答案已被消耗，必須換一張否則使用者輸入什麼都會錯
  field.captcha = null
  onGenerateCaptcha()
  active.error = data.code
  active.xhring = false
}

/**
 * 更換 - 圖形驗證碼
 */
const onGenerateCaptcha = () => (active.captcha = getUuid(16, true))

/**
 * 圖形驗證碼 - 網址
 */
const getCaptcha = computed(() => `${ import.meta.env.VITE_BASE_URL }secure/captcha?rt=${ active.captcha }`)

/**
 * 獲取 Logo
 */
const getLogo = computed(() => {
  if (active.data.organization.seq) return `/img/organization/${ active.data.organization.seq }/logo.png`
  return "/img/organization/1/logo.png"
})

/**
 * 獲取背景圖
 */
const getBackground = computed(() => {
  if (active.data.avatarUrl) return active.data.avatarUrl
  return "/img/avatar/holder/my.jpg"
})

/**
 * 存取 API - 教會資訊
 */
const fetch = async () => {
  const { data } = await createLogin(route.params.id)

  // 返回錯誤不往下進行
  if (data?.data === undefined) return

  active.data.organization = data.data.organization
  active.data.available = data.data.available
  active.data.name = data.data.name
  active.data.uuid = data.data.uuid
  active.data.avatarUrl = data.data.avatarUrl

  // 可以打開頁面了
  active.loaded = true
}

/**
 * 生命週期 - 掛載完成
 */
onMounted(() => {
  // 如果已經登入, 前往控制面板
  if (isLogin.value) router.replace({ name: RouteNameEnum.ADMIN_DASHBOARD })
  // 未登入
  else fetch()
})

</script>

<style scoped lang="scss">
// 標籤
.f-label {
  display: block;
  margin-bottom: 0.4rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #5e5873;
}

// 輸入框
.f-input {
  width: 100%;
  border-radius: 0.5rem;
  border: 1px solid #d8d6de;
  background: #fff;
  padding: 0.65rem 1rem;
  font-size: 0.9rem;
  color: #5e5873;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: #b9b9c3;
  }

  &:focus {
    outline: none;
    border-color: #dda026;
    box-shadow: 0 0 0 3px rgba(221, 160, 38, 0.18);
  }

  &--error {
    border-color: #ea5455;
  }
}

// 錯誤訊息
.f-error {
  margin: 0.35rem 0 0;
  font-size: 0.75rem;
  color: #ea5455;
}

// 送出按鈕（品牌金漸層）
.f-btn {
  width: 100%;
  border: 0;
  border-radius: 0.5rem;
  padding: 0.7rem 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  background: linear-gradient(118deg, #dda026, rgba(221, 160, 38, 0.7));
  box-shadow: 0 8px 16px -6px rgba(221, 160, 38, 0.5);
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
}

// 深色模式
:where(.tw-dark-layout) {
  .f-label {
    color: #d0d2d6;
  }

  .f-input {
    border-color: #3b4253;
    background: #283046;
    color: #d0d2d6;

    &::placeholder {
      color: #676d7d;
    }
  }
}
</style>
